import React, { createContext, useState, useEffect } from "react";
// import logger from "../../../backend/utils/logger";

const UserContext = createContext({
    user: null,
    profile: null,
    permissions: [],
    setUser: () => {},
    setProfile: (profile) => {},
    setPermissions: () => {},
    login: () => {},
    logout: () => {},
});

export const UserProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [profile, setProfile] = useState(null);
    const [permissions, setPermissions] = useState([]);

    useEffect(() => {
        const loadUserData = async () => {
            // Load user data from localStorage on app start
            const token = localStorage.getItem('token');
            const savedPermissions = localStorage.getItem('userPermissions');

            if (token) {
                // Load basic user info
                const userData = { email: localStorage.getItem('userEmail') || '' };
                setUser(userData);

                // Always refresh permissions from server on app start
                try {
                    const PermissionService = (await import('../services/permissionService')).default;
                    const permissionResponse = await PermissionService.getUserPermissions();

                    // permissionResponse from service is now the normalized string[]
                    const userPermissions = Array.isArray(permissionResponse)
                        ? permissionResponse
                        : [];

                    console.log('PermissionService getUserPermissions response:', permissionResponse);
                    console.log('Loaded normalized permissions on app start:', userPermissions);

                    setPermissions(userPermissions);
                    localStorage.setItem('userPermissions', JSON.stringify(userPermissions));

                    // Also load profile data
                    try {
                        const ProfileService = (await import('../services/profileService')).default;
                        const profileData = await ProfileService.getProfile();
                        console.log('UserContext: Profile loaded on app start:', profileData);
                        setProfile(profileData);
                    } catch (profileError) {
                        console.error('Failed to load profile on app start:', profileError);
                    }
                } catch (error) {
                    console.error('Failed to load permissions on app start:', error);
                    // Try again after a short delay (server might still be starting)
                    setTimeout(async () => {
                        try {
                            const PermissionService = (await import('../services/permissionService')).default;
                            const permissionResponse = await PermissionService.getUserPermissions();

                            const userPermissions = Array.isArray(permissionResponse)
                                ? permissionResponse
                                : [];

                            console.log('PermissionService getUserPermissions retry response:', permissionResponse);
                            console.log('Loaded normalized permissions after retry:', userPermissions);

                            setPermissions(userPermissions);
                            localStorage.setItem('userPermissions', JSON.stringify(userPermissions));

                            // Also load profile data on retry
                            try {
                                const ProfileService = (await import('../services/profileService')).default;
                                const profileData = await ProfileService.getProfile();
                                console.log('UserContext: Profile loaded on retry:', profileData);
                                setProfile(profileData);
                            } catch (profileError) {
                                console.error('Failed to load profile on retry:', profileError);
                            }
                        } catch (retryError) {
                            console.error('Failed to load permissions after retry:', retryError);
                            // Fallback to saved permissions
                            if (savedPermissions) {
                                try {
                                    setPermissions(JSON.parse(savedPermissions));
                                } catch (parseError) {
                                    console.error('Error parsing saved permissions:', parseError);
                                    setPermissions([]);
                                }
                            } else {
                                setPermissions([]);
                            }
                        }
                    }, 2000); // Wait 2 seconds for server to be ready
                }
            }
        };

        loadUserData();
    }, []);

    const login = async (userData) => {
        setUser(userData);
        localStorage.setItem('userEmail', userData.email || '');

        // Load permissions and profile immediately after login
        try {
            const PermissionService = (await import('../services/permissionService')).default;
            const permissionResponse = await PermissionService.getUserPermissions();

            const userPermissions = Array.isArray(permissionResponse)
                ? permissionResponse
                : [];

            setPermissions(userPermissions);
            localStorage.setItem('userPermissions', JSON.stringify(userPermissions));

            // Also load profile data
            try {
                const ProfileService = (await import('../services/profileService')).default;
                const profileData = await ProfileService.getProfile();
                console.log('UserContext: Profile loaded after login:', profileData);
                setProfile(profileData);
            } catch (profileError) {
                console.error('Failed to load profile after login:', profileError);
            }
        } catch (error) {
            console.error('Failed to load permissions after login:', error);
            setPermissions([]);
        }
    };

    const logout = () => {
        setUser(null);
        setProfile(null);
        setPermissions([]);
        localStorage.removeItem('userEmail');
        localStorage.removeItem('userPermissions');
    };

    return React.createElement(
        UserContext.Provider,
        { value: { user, profile, permissions, setUser, setProfile, setPermissions, login, logout } },
        children
    );
};

export { UserContext };
export default UserContext;
